import { EntityResolved } from '@api';
import { Button } from '@design/components/button/Button';
import { Icon } from '@design/components/icon/Icon';
import { useQueryParams } from '@terminal/hooks/use-query-params/useQueryParams';
import { useAssetsFilterEntityTags } from '@terminal/react-query-hooks/useAssetsFilterEntityTags';
import { queryKeys } from '@terminal/routes/query-params';
import { postMultipleEntities } from '@terminal/services/entities/entity-service';
import { Tracking } from '@terminal/services/tracking';
import { usePosthogEvents } from '@terminal/services/tracking/posthog/use-posthog-events/usePosthogEvents';
import { useWatchlist } from '@terminal/stores/use-watchlist/useWatchlist';
import { WatchlistError } from '@terminal/stores/use-watchlist/watchlist-types';
import { useWatchlistManagerWidget } from '@terminal/stores/use-watchlist-manager-widget/useWatchlistManagerWidget';
import { AlertMessage } from '@terminal/views/onboarding/alert-message/AlertMessage';
import { useAlertMessage } from '@terminal/views/onboarding/hooks/useAlertMessage';
import clsx from 'clsx';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { ChatWatchlistManager } from '../chat-watchlist-manager-widget/ChatWatchlistManagerWidget';
import { TableWatchlistManagerWidget } from '../table-watchlist-manager-widget/TableWatchlistManagerWidget';
import { AssetFilter } from './components/asset-filter/AssetFilter';
import { ConfirmationModal } from './components/confirmation-modal/ConfirmationModal';
import { UpgradePrompt } from './components/upgrade-prompt/UpgradePrompt';
import { WatchlistManagerHeader } from './components/watchlist-manager-header/WatchlistManagerHeader';
import { useEntityChangeHandlers } from './useEntityChangeHandlers';
import { useEntityResolution } from './useEntityResolution';
import { useNavigationBlocker } from './useNavigationBlocker';
import { useWatchlistForm } from './useWatchlistForm';
import styles from './WatchlistManagerWidget.module.scss';

export interface WatchlistManagerWidgetProps {
  onListCreated: (id: string) => void;
  onCancel?: () => void;
  className?: string;
}

export const WatchlistManagerWidget = ({
  className,
  onListCreated,
  onCancel,
}: WatchlistManagerWidgetProps) => {
  const { t } = useTranslation(['watchlistManager', 'common', 'widget']);
  const unMappedRowRef = useRef<HTMLTableRowElement>(null);
  const hasPrefilledEditModeRef = useRef(false);

  const [chatInputState, setChatInputState] = useState({
    value: '',
    isAssetMessage: true,
  });
  const [previousEntityTagsFromFilter, setPreviousEntityTagsFromFilter] =
    useState<string[]>([]);
  const containerRef = useRef<HTMLDivElement>(null);
  const { watchlistId: watchlistIdFromRoute } = useParams();
  const { params } = useQueryParams();
  const watchlistIdFromQuery = params[queryKeys.watchlistId];
  const watchlistId = watchlistIdFromQuery ?? watchlistIdFromRoute;
  const { lists, updateList, filterGroups, resetFilterGroups } = useWatchlist(
    state => ({
      lists: state.lists,
      updateList: state.updateList,
      filterGroups: state.filterGroups,
      resetFilterGroups: state.resetFilterGroups,
    })
  );
  const { trackWatchlistCreate } = usePosthogEvents();

  const { data: entityTagsData, isLoading: isEntityTagsLoading } =
    useAssetsFilterEntityTags(filterGroups);

  const {
    // State
    listName,
    isRenameActive,
    entitiesList,
    unMappedEntitiesList,
    resolvedEntityData,
    allDuplicateTickers,
    rowSelection,
    countEntitiesAdded,
    savingInProgress,
    // Error state
    watchlistError,
    watchlistErrorText,
    hasExceededUsageLimit,
    // Loading state
    createListLoading,
    // Computed
    hasUnsavedChanges,
    hasEntities,
    selectedEntitiesCount,
    // Actions
    setIsRenameActive,
    onUpdateName,
    resetName,
    setSavingInProgress,
    setWatchlistError,
    setResolvedEntityData,
    setEntitiesList,
    setUnMappedEntitiesList,
    setAllDuplicateTickers,
    clearStore,
    updateEntitiesList,
    entityAddedClear,
    clearEntityAddedTimeout,
    validateForm,
    handleValidationError,
    onToggleAllRows,
    onRowSelectionChange,
    createNewList,
    setRowSelection,
  } = useWatchlistForm();

  const { isPending, onChatSubmit, onSelectItem, getIsInWatchlist } =
    useEntityResolution({
      entitiesList,
      unMappedEntitiesList,
      setResolvedEntityData,
      setUnMappedEntitiesList,
      setAllDuplicateTickers,
      setWatchlistError,
      updateEntitiesList,
      entityAddedClear,
    });

  const { alertMessage, setMessage, setIsError } = useAlertMessage({
    resolvedEntityData,
    allDuplicateTickers,
  });

  const { isAddMode, isEditMode, onConfirmCallback, shouldResetList } =
    useWatchlistManagerWidget(state => ({
      isAddMode: state.isAddMode,
      isEditMode: state.isEditMode,
      onConfirmCallback: state.onConfirmCallback,
      shouldResetList: state.shouldResetList,
    }));

  useEffect(() => setMessage(''), []);

  useEffect(() => {
    return () => {
      clearStore();
      resetFilterGroups();
      clearEntityAddedTimeout();
      hasPrefilledEditModeRef.current = false;
    };
  }, []);

  // Entity tags from asset filter
  useEffect(() => {
    if (entityTagsData.length < previousEntityTagsFromFilter.length) {
      const toRemoveTagsFromFilter = previousEntityTagsFromFilter.filter(
        tag => !entityTagsData.includes(tag)
      );

      const newEntitiesList = entitiesList.filter(
        e => !toRemoveTagsFromFilter.includes(e.tag)
      );
      setEntitiesList(newEntitiesList);
      setUnMappedEntitiesList([]);
      setPreviousEntityTagsFromFilter(entityTagsData);
    } else {
      const newEntityTagsFromFilter = entityTagsData.filter(
        tag => !previousEntityTagsFromFilter.includes(tag)
      );

      if (!isEntityTagsLoading && newEntityTagsFromFilter.length > 0) {
        setPreviousEntityTagsFromFilter(entityTagsData);
        postMultipleEntities(newEntityTagsFromFilter)
          .then(fullEntities => {
            const newEntities = fullEntities.map(
              (e): EntityResolved => ({ ...e, input: '' })
            );
            updateEntitiesList({ newEntities, entitiesList });
            entityAddedClear(newEntities.length);
          })
          .catch(error => {
            Tracking.captureException(error, {
              context: 'WatchlistManagerWidget - Error fetching entities',
            });
          });
      }
    }
  }, [isEntityTagsLoading, entityTagsData.length]);

  const entityChangeHandlers = useEntityChangeHandlers(setRowSelection);

  const onConfirmSuccess = (id: string) => {
    resetName();
    updateEntitiesList({ entitiesList: [] });
    onListCreated(id);
  };

  const activeListInAddMode =
    isAddMode && watchlistId
      ? lists.find(list => list.id === watchlistId)
      : undefined;

  const activeListInEditMode =
    isEditMode && watchlistId
      ? lists.find(list => list.id === watchlistId)
      : undefined;

  const activeTags = shouldResetList
    ? []
    : activeListInAddMode?.entities.map(e => e.tag) || [];

  useEffect(
    function prefillEditModeWatchlistTable() {
      if (
        !isEditMode ||
        !activeListInEditMode ||
        hasPrefilledEditModeRef.current
      ) {
        return;
      }

      hasPrefilledEditModeRef.current = true;
      const tags = activeListInEditMode.entities.map(e => e.tag);

      if (!tags.length) {
        return;
      }

      postMultipleEntities(tags)
        .then(fullEntities => {
          const resolved = fullEntities.map(
            (e): EntityResolved => ({ ...e, input: '' })
          );
          setEntitiesList(resolved);
          setRowSelection(Object.fromEntries(resolved.map(e => [e.tag, true])));
        })
        .catch(error => {
          Tracking.captureException(error, {
            context: 'WatchlistManagerWidget - Error pre-filling edit mode',
          });
        });
    },
    [isEditMode, activeListInEditMode, setEntitiesList, setRowSelection]
  );

  const onConfirm = useCallback(async (): Promise<string | null> => {
    setSavingInProgress(true);

    const { isValid, tags, unresolvedTags, error } = validateForm({
      isAddMode: !!isAddMode,
      isEditMode: !!activeListInEditMode,
    });

    if (!isValid) {
      handleValidationError(error);
      setSavingInProgress(false);
      return null;
    }

    if (activeListInEditMode) {
      await updateList({
        id: activeListInEditMode.id,
        name: activeListInEditMode.name,
        tags,
      });
      onConfirmCallback?.();
      setSavingInProgress(false);

      return activeListInEditMode.id;
    }

    if (activeListInAddMode && watchlistId) {
      await updateList({
        id: watchlistId,
        name: activeListInAddMode.name,
        tags: [...tags, ...activeTags],
      });
      onConfirmCallback?.();
      setSavingInProgress(false);

      return watchlistId;
    }

    const listId = await createNewList(listName, tags, unresolvedTags);
    setSavingInProgress(false);

    if (listId) {
      trackWatchlistCreate('watchlist');
    }

    return listId;
  }, [
    setSavingInProgress,
    validateForm,
    isAddMode,
    handleValidationError,
    activeListInEditMode,
    activeListInAddMode,
    watchlistId,
    updateList,
    activeTags,
    onConfirmCallback,
    createNewList,
    listName,
    trackWatchlistCreate,
  ]);

  const { isBlocked, confirmAndProceed, proceedWithoutSaving, resetBlocker } =
    useNavigationBlocker({
      savingInProgress,
      hasUnsavedChanges,
      hasExceededUsageLimit,
      onConfirm,
    });

  const onChatInputValueChange = (value: string, isAssetMessage = false) => {
    setChatInputState({ value, isAssetMessage });
  };

  const onSave = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();
    const newId = await onConfirm();

    if (newId) {
      onConfirmSuccess(newId);
    }
  };

  const clearAlertMessage = () => {
    setMessage('');
    setIsError(false);
  };

  const showActionButtons =
    hasEntities && (chatInputState.isAssetMessage || !chatInputState.value);

  const isEntitiesOverflowing =
    !!containerRef.current &&
    containerRef.current?.scrollHeight > containerRef.current?.clientHeight;

  return (
    <div
      className={clsx(styles.container, className)}
      data-testid="watchlist-manager-widget"
    >
      <AlertMessage
        tableRef={unMappedRowRef}
        alertMessage={alertMessage}
        clearMessage={clearAlertMessage}
        isEntitiesOverflowing={isEntitiesOverflowing}
        insideModal
      />
      <WatchlistManagerHeader
        listName={listName}
        onUpdateName={onUpdateName}
        isRenameActive={isRenameActive}
        setIsRenameActive={setIsRenameActive}
        hasEntities={hasEntities}
        hasInputError={!!watchlistErrorText}
        activeListName={activeListInEditMode?.name ?? activeListInAddMode?.name}
      />
      <div>
        <AssetFilter isPending={isPending} />
      </div>
      <div className={styles.separator}>
        <div className={styles.separatorLine} />
        <span className={styles.separatorText}>{t('common:or')}</span>
        <div className={styles.separatorLine} />
      </div>
      <div className={styles.contentContainer}>
        <TableWatchlistManagerWidget
          data={entitiesList}
          countEntitiesAdded={countEntitiesAdded}
          toggleAllRowsSelection={onToggleAllRows}
          rowSelection={rowSelection}
          updateRowSelection={onRowSelectionChange}
          containerRef={containerRef}
          unMappedRowRef={unMappedRowRef}
          isEmptyListError={watchlistError === WatchlistError.EmptyList}
          handlers={entityChangeHandlers}
          showIndustryColumn
          isPending={isPending}
        />
        {!hasExceededUsageLimit && (
          <>
            {hasEntities && (
              <p className={styles.label}>
                {t('watchlistManager:wantAddMore')}
              </p>
            )}
            <ChatWatchlistManager
              value={chatInputState.value}
              isPending={isPending}
              getIsInWatchlist={getIsInWatchlist}
              onSelectItem={onSelectItem}
              onChatSubmit={onChatSubmit}
              onChange={onChatInputValueChange}
              activeListEntityTags={activeTags}
            />
          </>
        )}
      </div>
      <UpgradePrompt hide={!hasExceededUsageLimit} />
      {showActionButtons && (
        <div className={styles.footerContainer}>
          {watchlistErrorText && (
            <>
              <Icon
                className={styles.styledIcon}
                size={20}
                iconName="ExclamationCircle"
                fillColor="var(--icon-critical)"
              />
              <p className={styles.errorMessage} data-testid="error-message">
                {watchlistErrorText}
              </p>
            </>
          )}
          <div className={styles.flexContainer}>
            {onCancel && (
              <Button
                label={t('common:cancel')}
                variant="tertiary"
                size="large"
                onClick={onCancel}
              />
            )}
            <Button
              label={
                activeListInEditMode
                  ? t('widget:watchlist.saveChanges')
                  : activeListInAddMode
                  ? t(
                      'watchlistManager:confirmationModal.addAssetsToWatchlist',
                      {
                        count: selectedEntitiesCount,
                      }
                    )
                  : t('watchlistManager:confirmationModal.save')
              }
              variant="primary"
              size="large"
              onClick={onSave}
              isLoading={createListLoading}
              disabled={createListLoading || hasExceededUsageLimit}
              data-testid="watchlist-manager-confirm-button"
            />
          </div>
        </div>
      )}
      {isBlocked && (
        <ConfirmationModal
          onCancel={proceedWithoutSaving}
          onClose={resetBlocker}
          onConfirm={confirmAndProceed}
          hasActiveListInAddMode={!!activeListInAddMode}
          selectedEntitiesCount={selectedEntitiesCount}
        />
      )}
    </div>
  );
};
